export const DRAFT_PREFIX = "FieldMapDraft";
export const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
export const TRANSFORM_KIND_PREDEFINED = "PredefinedJavaScript";
export const TRANSFORM_KIND_CUSTOM = "CustomJavaScript";
export const TRANSFORM_KIND_SOURCE_LOOKUP = "SourceObjectLookup";
export const TRANSFORM_KIND_ENTITY_LOOKUP = "EntityObjectLookup";

export function asArray(value) {
  return Array.isArray(value) ? value : [];
}

export function dedupeTransforms(transforms) {
  const seen = new Set();
  return asArray(transforms).filter((transform) => {
    const key = [
      transform?.key || transform?.TransformKey || "",
      transform?.name || transform?.displayName || transform?.Text || "",
      transform?.group || transform?.Group || "",
      transform?.code || transform?.Script || transform?.Code || "",
    ]
      .map((value) => String(value || "").trim().toLowerCase())
      .join("|");
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

const makeTempId = (prefix) => `${prefix}_${Math.random().toString(36).slice(2)}`;
const isTempItemId = (value) => String(value || "").startsWith("item_");

const getFallbackPersistedId = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized || isTempItemId(normalized)) return null;
  return normalized.replace(/__dup\d+$/i, "");
};

export function ensureUniqueMapItemIds(items) {
  const seen = new Map();
  return asArray(items).map((item) => {
    const baseId = String(item?.id || makeTempId("item"));
    const nextIndex = (seen.get(baseId) || 0) + 1;
    seen.set(baseId, nextIndex);
    return {
      ...item,
      id: nextIndex === 1 ? baseId : `${baseId}__dup${nextIndex}`,
      persistedId: item?.persistedId || getFallbackPersistedId(item?.id) || null,
    };
  });
}

export function firstId(item) {
  return (
    item?._id ||
    item?.UniqueId ||
    item?.Id ||
    item?.id ||
    item?.EntityFieldMapId ||
    item?.EntityObjectFieldMapId ||
    null
  );
}

export function normalizeDataTypeLabel(value) {
  return String(value || "").replace(/^System\./i, "");
}

export function normalizeField(raw) {
  return {
    id: raw?._id || raw?.UniqueId || raw?.Id || raw?.id || raw?.EntityObjectFieldId || raw?.SourceObjectFieldId || raw?.FieldId || null,
    fieldName: raw?.FieldName || raw?.Name || raw?.SystemName || "",
    displayName: raw?.DisplayName || raw?.Label || raw?.Name || raw?.FieldName || "",
    dataTypeId: raw?.DataTypeId || raw?.DataType?._id || raw?.DataType?.UniqueId || raw?.DataType?.Id || raw?.DataType,
    dataTypeName: normalizeDataTypeLabel(raw?.DataTypeName || raw?.DataType?.DisplayName || raw?.DataType?.Name || ""),
    description: raw?.Description || raw?.HelpText || "",
    aiPromptContext: raw?.AIPromptContext || "",
    format: raw?.Format || "",
    isActive: raw?.IsActive !== false,
    raw,
  };
}

export function normalizeEntityObject(raw) {
  return {
    id: raw?._id || raw?.UniqueId || raw?.Id || raw?.id || raw?.EntityObjectId || null,
    displayName: raw?.DisplayName || raw?.Name || "Entity Object",
    objectName: raw?.ObjectName || raw?.Name || "",
    fields: asArray(raw?.Fields).map(normalizeField).filter((x) => x.id && x.isActive),
    raw,
  };
}

export function normalizeSourceObject(raw) {
  return {
    id: raw?._id || raw?.UniqueId || raw?.Id || raw?.id || raw?.SourceObjectId || null,
    displayName: raw?.DisplayName || raw?.Name || "Source Object",
    objectName: raw?.ObjectName || raw?.Name || "",
    fields: asArray(raw?.Fields).map(normalizeField).filter((x) => x.id && x.isActive),
    raw,
  };
}

const getRawTransformKind = (step) => {
  const explicitKind = step?.TransformKind || step?.transformKind || null;
  if (explicitKind) return explicitKind;

  const lookupObjectType = String(step?.LookupObjectType || step?.lookupObjectType || "").trim().toLowerCase();
  if (step?.LookupEntityObjectId || step?.lookupEntityObjectId) return TRANSFORM_KIND_ENTITY_LOOKUP;
  if (step?.LookupSourceObjectId || step?.lookupSourceObjectId) return TRANSFORM_KIND_SOURCE_LOOKUP;
  if (lookupObjectType === "entityobject") return TRANSFORM_KIND_ENTITY_LOOKUP;
  if (lookupObjectType === "sourceobject") return TRANSFORM_KIND_SOURCE_LOOKUP;

  const code = step?.CustomTransformScript || step?.customTransformScript || step?.TransformCode || step?.transformCode || "";
  return String(code || "").trim() ? TRANSFORM_KIND_CUSTOM : TRANSFORM_KIND_PREDEFINED;
};

const toPipelineStepKind = (transformKind, transformKey, code) => {
  if (transformKind === TRANSFORM_KIND_SOURCE_LOOKUP) return "lookup_source";
  if (transformKind === TRANSFORM_KIND_ENTITY_LOOKUP) return "lookup_entity";
  if (transformKind === TRANSFORM_KIND_CUSTOM || String(transformKey || "").toLowerCase() === "custom" || String(code || "").trim()) {
    return "custom";
  }
  return "predefined";
};

function normalizeTransformSteps(raw) {
  const scripts = asArray(raw?.TransformScripts || raw?.transformScripts || raw?.TransformSteps || raw?.transformSteps);
  return scripts
    .map((step, index) => {
      const code = step?.CustomTransformScript || step?.customTransformScript || step?.TransformCode || step?.transformCode || "";
      const customName = step?.CustomName || step?.customName || step?.TransformDisplayName || step?.transformDisplayName || "";
      const transformKey = step?.TransformKey || step?.transformKey || step?.TransformScriptId || step?.transformScriptId || null;
      const transformKind = getRawTransformKind(step);
      const kind = toPipelineStepKind(transformKind, transformKey, code);
      return {
        id: firstId(step) || `step_${Math.random().toString(36).slice(2)}`,
        kind,
        transformKind,
        key: transformKey,
        displayName:
          customName ||
          (kind === "custom"
            ? "Custom"
            : kind === "lookup_entity"
              ? "Entity Lookup"
              : kind === "lookup_source"
                ? "Source Lookup"
                : String(transformKey || "").trim()),
        code,
        isActive: step?.IsActive !== false && step?.isActive !== false,
        lookupObjectType:
          step?.LookupObjectType ||
          step?.lookupObjectType ||
          (step?.LookupEntityObjectId || step?.lookupEntityObjectId
            ? "EntityObject"
            : step?.LookupSourceObjectId || step?.lookupSourceObjectId
              ? "SourceObject"
              : null),
        lookupObjectId:
          step?.LookupObjectId ||
          step?.lookupObjectId ||
          step?.LookupEntityObjectId ||
          step?.lookupEntityObjectId ||
          step?.LookupSourceObjectId ||
          step?.lookupSourceObjectId ||
          null,
        lookupSearchFieldId:
          step?.LookupSearchFieldId ||
          step?.lookupSearchFieldId ||
          step?.LookupEntitySearchFieldId ||
          step?.lookupEntitySearchFieldId ||
          step?.LookupSourceSearchFieldId ||
          step?.lookupSourceSearchFieldId ||
          null,
        lookupResultFieldId:
          step?.LookupResultFieldId ||
          step?.lookupResultFieldId ||
          step?.LookupEntityResultFieldId ||
          step?.lookupEntityResultFieldId ||
          step?.LookupSourceResultFieldId ||
          step?.lookupSourceResultFieldId ||
          null,
        lookupMatchMethod: step?.LookupMatchMethod || step?.lookupMatchMethod || "==",
        lookupIgnoreCase: step?.LookupIgnoreCase !== false && step?.lookupIgnoreCase !== false,
        lookupNoMatchBehavior: step?.LookupNoMatchBehavior || step?.lookupNoMatchBehavior || "ReturnNull",
        lookupMultipleMatchBehavior: step?.LookupMultipleMatchBehavior || step?.lookupMultipleMatchBehavior || "First",
        lookupPageSize: Number(step?.LookupPageSize || step?.lookupPageSize || 1000) || 1000,
        lookupMaxPages: Number(step?.LookupMaxPages || step?.lookupMaxPages || 100) || 100,
        raw: step || null,
      };
    })
    .filter((step) => step.kind !== "predefined" || step.key || step.code);
}

export function normalizeMapItem(raw, forcedEntityFieldId = null) {
  const persistedId = firstId(raw) || null;
  const sourceFieldId = raw?.SourceFieldId || raw?.SourceObjectFieldId || raw?.sourceFieldId || null;
  const sourceObjectId = raw?.SourceObjectId || raw?.sourceObjectId || null;
  const isConstant = raw?.IsConstant === true || sourceFieldId === EMPTY_GUID;
  const rawImportance = Number(raw?.Importance ?? raw?.importance);
  const importance = Number.isFinite(rawImportance) && rawImportance >= 1 && rawImportance <= 255 ? Math.round(rawImportance) : 255;
  const transformSteps = normalizeTransformSteps(raw);
  const firstStep = transformSteps[0] || null;
  const legacyCustomTransformScript = raw?.CustomTransformScript || raw?.customTransformScript || "";
  const isDeleted = raw?.IsDeleted === true || raw?.isDeleted === true;
  const isActive = !isDeleted && raw?.IsActive !== false && raw?.isActive !== false;
  return {
    id: persistedId || makeTempId("item"),
    persistedId,
    sourceFieldId,
    sourceObjectId,
    entityFieldId: forcedEntityFieldId || raw?.EntityFieldId || raw?.EntityObjectFieldId || raw?.entityFieldId || null,
    transformKey: raw?.TransformKey || raw?.transformKey || firstStep?.key || null,
    transformCode: raw?.TransformCode || raw?.transformCode || raw?.CustomTransformScript || raw?.customTransformScript || firstStep?.code || "",
    transformDisplayName: raw?.TransformDisplayName || raw?.transformDisplayName || firstStep?.displayName || "",
    transformSteps,
    multiTransformMode: String(legacyCustomTransformScript || "").trim().length === 0,
    legacyCustomTransformScript,
    constantValue: raw?.ConstantValue || raw?.constantValue || raw?.SpecialValue || raw?.specialValue || "",
    importance,
    isConstant,
    isActive,
    isDeleted,
    raw,
  };
}

export function normalizeFieldMap(raw, entityObjectId) {
  if (raw?.IsDeleted === true || raw?.isDeleted === true || raw?.IsActive === false || raw?.isActive === false) {
    return buildEmptyFieldMap(raw?.EntityObjectId || raw?.entityObjectId || entityObjectId || null, raw?.SourceObjectId || raw?.sourceObjectId || null);
  }
  const id = firstId(raw);
  const mapEntityId = raw?.EntityObjectId || raw?.entityObjectId || entityObjectId || null;
  const childSourceObjectIds = Array.from(
    new Set(
      asArray(raw?.FieldMap)
        .map((x) => x?.SourceObjectId || x?.sourceObjectId || null)
        .filter((x) => Boolean(x) && x !== EMPTY_GUID)
    )
  );
  const sourceObjectId = (raw?.SourceObjectId || raw?.sourceObjectId || childSourceObjectIds[0] || null) === EMPTY_GUID
    ? null
    : (raw?.SourceObjectId || raw?.sourceObjectId || childSourceObjectIds[0] || null);
  const fromItems = asArray(raw?.Items)
    .map(normalizeMapItem)
    .filter((x) => (x.sourceFieldId || x.isConstant) && x.entityFieldId);
  const fromFieldMap = asArray(raw?.FieldMap)
    .map((x) => normalizeMapItem(x, raw?.EntityObjectFieldId || raw?.entityFieldId || null))
    .filter((x) => (x.sourceFieldId || x.isConstant) && x.entityFieldId);

  return {
    id,
    entityObjectId: mapEntityId,
    sourceObjectId,
    sourceObjectIds: childSourceObjectIds,
    items: ensureUniqueMapItemIds(fromItems.length > 0 ? fromItems : fromFieldMap),
    raw,
  };
}

export function buildEmptyFieldMap(entityObjectId, sourceObjectId) {
  return {
    id: null,
    entityObjectId,
    sourceObjectId,
    items: [],
    parentFieldMapsByEntityFieldId: {},
    raw: null,
    isNew: true,
  };
}

export function replaceMapping(items, nextItem, options = {}) {
  const allowMultipleEntitiesPerSourceField = options?.allowMultipleEntitiesPerSourceField === true;
  const clean = asArray(items).filter((x) => {
    if (x.id === nextItem.id) return false;

    // Constant mappings use EMPTY_GUID as source. They should only replace by entity field.
    if (nextItem?.isConstant) return x.entityFieldId !== nextItem.entityFieldId;

    if (allowMultipleEntitiesPerSourceField) {
      // Keep source duplicates, but enforce one mapping per entity field.
      return x.entityFieldId !== nextItem.entityFieldId;
    }

    return x.sourceFieldId !== nextItem.sourceFieldId && x.entityFieldId !== nextItem.entityFieldId;
  });
  clean.push(nextItem);
  return clean;
}

export function draftKey(entityObjectId, sourceObjectId, mapIdOrNew) {
  return `${DRAFT_PREFIX}:${entityObjectId}:${sourceObjectId}:${mapIdOrNew || "new"}`;
}

const toDraftTransformStep = (step) => ({
  id: step?.id || null,
  kind: step?.kind || null,
  transformKind: step?.transformKind || null,
  key: step?.key || null,
  displayName: step?.displayName || "",
  code: step?.code || "",
  isActive: step?.isActive !== false,
  lookupObjectType: step?.lookupObjectType || null,
  lookupObjectId: step?.lookupObjectId || null,
  lookupSearchFieldId: step?.lookupSearchFieldId || null,
  lookupResultFieldId: step?.lookupResultFieldId || null,
  lookupMatchMethod: step?.lookupMatchMethod || "==",
  lookupIgnoreCase: step?.lookupIgnoreCase !== false,
  lookupNoMatchBehavior: step?.lookupNoMatchBehavior || "ReturnNull",
  lookupMultipleMatchBehavior: step?.lookupMultipleMatchBehavior || "First",
  lookupPageSize: step?.lookupPageSize || 1000,
  lookupMaxPages: step?.lookupMaxPages || 100,
});

const toDraftMapItem = (item, fallbackSourceObjectId = null) => ({
  id: item?.id || null,
  persistedId: item?.persistedId || null,
  sourceFieldId: item?.sourceFieldId || null,
  sourceObjectId: item?.sourceObjectId || item?.raw?.SourceObjectId || fallbackSourceObjectId || null,
  entityFieldId: item?.entityFieldId || null,
  transformKey: item?.transformKey || null,
  transformCode: item?.transformCode || "",
  transformDisplayName: item?.transformDisplayName || "",
  transformSteps: asArray(item?.transformSteps).map(toDraftTransformStep),
  multiTransformMode: item?.multiTransformMode !== false,
  legacyCustomTransformScript: item?.legacyCustomTransformScript || "",
  constantValue: item?.constantValue || "",
  importance: item?.importance,
  isConstant: item?.isConstant === true,
  isActive: item?.isActive !== false,
  isDeleted: item?.isDeleted === true,
});

export function toDraftMap(map) {
  return {
    id: map?.id || null,
    entityObjectId: map?.entityObjectId || null,
    sourceObjectId: map?.sourceObjectId || null,
    isNew: map?.isNew === true,
    items: asArray(map?.items).map((item) => toDraftMapItem(item, map?.sourceObjectId || null)),
  };
}

export function rehydrateDraftMap(draft, baseMap) {
  const fallbackMap = buildEmptyFieldMap(baseMap?.entityObjectId, baseMap?.sourceObjectId);
  const startingMap = baseMap || fallbackMap;
  const baseItemsById = new Map(asArray(startingMap?.items).map((item) => [item?.id, item]));
  const draftItems = asArray(draft?.items)
    .map((item) => {
      const baseItem = baseItemsById.get(item?.id) || null;
      return {
        ...(baseItem || {}),
        ...item,
        persistedId: item?.persistedId || baseItem?.persistedId || null,
        sourceObjectId: item?.sourceObjectId || baseItem?.sourceObjectId || startingMap?.sourceObjectId || null,
        transformSteps: asArray(item?.transformSteps).map((step) => ({ ...(step || {}) })),
        raw: baseItem?.raw || null,
      };
    })
    .filter((item) => !item?.isDeleted && item?.entityFieldId && (item?.sourceFieldId || item?.isConstant === true));

  return {
    ...startingMap,
    id: draft?.id || startingMap?.id || null,
    entityObjectId: draft?.entityObjectId || startingMap?.entityObjectId || null,
    sourceObjectId: draft?.sourceObjectId || startingMap?.sourceObjectId || null,
    items: ensureUniqueMapItemIds(draftItems),
  };
}

export function searchFieldMatch(field, term) {
  const q = String(term || "").trim().toLowerCase();
  if (!q) return true;
  return (
    String(field?.fieldName || "")
      .toLowerCase()
      .includes(q) ||
    String(field?.displayName || "")
      .toLowerCase()
      .includes(q) ||
    String(field?.dataTypeName || "")
      .toLowerCase()
      .includes(q)
  );
}

const normalizeImportance = (value, fallback = 255) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  const rounded = Math.round(parsed);
  if (rounded < 1) return 1;
  if (rounded > 255) return 255;
  return rounded;
};

const toTransformScripts = (item) => {
  if (item?.multiTransformMode === false) return [];
  return asArray(item?.transformSteps).map((step, index) => ({
    ...(step?.raw || {}),
    _id: step?.id && !String(step.id).startsWith("step_") ? step.id : undefined,
    Sequence: index + 1,
    CustomName: step?.displayName || "",
    TransformKind:
      step?.transformKind ||
      (step?.kind === "lookup_entity"
        ? TRANSFORM_KIND_ENTITY_LOOKUP
        : step?.kind === "lookup_source"
          ? TRANSFORM_KIND_SOURCE_LOOKUP
          : step?.kind === "custom"
            ? TRANSFORM_KIND_CUSTOM
            : TRANSFORM_KIND_PREDEFINED),
    CustomTransformScript: step?.kind === "custom" ? (step?.code || "") : "",
    TransformScriptId:
      step?.kind === "predefined"
        ? (step?.key || step?.raw?.TransformScriptId || step?.raw?.transformScriptId)
        : undefined,
    LookupObjectType:
      step?.kind === "lookup_entity"
        ? "EntityObject"
        : step?.kind === "lookup_source"
          ? "SourceObject"
          : (step?.lookupObjectType || undefined),
    LookupObjectId:
      step?.kind === "lookup_entity" || step?.kind === "lookup_source"
        ? (step?.lookupObjectId || undefined)
        : undefined,
    LookupSearchFieldId:
      step?.kind === "lookup_entity" || step?.kind === "lookup_source"
        ? (step?.lookupSearchFieldId || undefined)
        : undefined,
    LookupResultFieldId:
      step?.kind === "lookup_entity" || step?.kind === "lookup_source"
        ? (step?.lookupResultFieldId || undefined)
        : undefined,
    LookupMatchMethod:
      step?.kind === "lookup_entity" || step?.kind === "lookup_source"
        ? (step?.lookupMatchMethod || "==")
        : undefined,
    LookupIgnoreCase:
      step?.kind === "lookup_entity" || step?.kind === "lookup_source"
        ? (step?.lookupIgnoreCase !== false)
        : undefined,
    LookupNoMatchBehavior:
      step?.kind === "lookup_entity" || step?.kind === "lookup_source"
        ? (step?.lookupNoMatchBehavior || "ReturnNull")
        : undefined,
    LookupMultipleMatchBehavior:
      step?.kind === "lookup_entity" || step?.kind === "lookup_source"
        ? (step?.lookupMultipleMatchBehavior || "First")
        : undefined,
    LookupPageSize:
      step?.kind === "lookup_entity" || step?.kind === "lookup_source"
        ? (step?.lookupPageSize || 1000)
        : undefined,
    LookupMaxPages:
      step?.kind === "lookup_entity" || step?.kind === "lookup_source"
        ? (step?.lookupMaxPages || 100)
        : undefined,
    LookupSourceObjectId: step?.kind === "lookup_source" ? (step?.lookupObjectId || undefined) : undefined,
    LookupSourceSearchFieldId: step?.kind === "lookup_source" ? (step?.lookupSearchFieldId || undefined) : undefined,
    LookupSourceResultFieldId: step?.kind === "lookup_source" ? (step?.lookupResultFieldId || undefined) : undefined,
    LookupEntityObjectId: step?.kind === "lookup_entity" ? (step?.lookupObjectId || undefined) : undefined,
    LookupEntitySearchFieldId: step?.kind === "lookup_entity" ? (step?.lookupSearchFieldId || undefined) : undefined,
    LookupEntityResultFieldId: step?.kind === "lookup_entity" ? (step?.lookupResultFieldId || undefined) : undefined,
    IsDeleted: false,
    IsActive: step?.isActive !== false,
  }));
};

const toSourceFieldMapRow = (x, isDeleted = false, fallbackSourceObjectId = null) => ({
  ...(x?.raw || {}),
  _id: x?.persistedId || (x?.id && !isTempItemId(x.id) ? getFallbackPersistedId(x.id) : undefined),
  SourceFieldId: x?.isConstant ? EMPTY_GUID : x?.sourceFieldId,
  SourceObjectFieldId: x?.isConstant ? EMPTY_GUID : x?.sourceFieldId,
  SourceObjectId: x?.raw?.SourceObjectId || x?.sourceObjectId || fallbackSourceObjectId || undefined,
  EntityFieldId: x?.entityFieldId,
  EntityObjectFieldId: x?.entityFieldId,
  TransformScripts: toTransformScripts(x),
  CustomTransformScript:
    x?.multiTransformMode === false
      ? String(x?.legacyCustomTransformScript ?? x?.transformCode ?? "")
      : "",
  ConstantValue: x?.constantValue || x?.specialValue || "",
  Importance: normalizeImportance(x?.importance, normalizeImportance(x?.raw?.Importance, 255)),
  IsConstant: x?.isConstant === true,
  IsActive: isDeleted ? false : x?.isActive !== false && x?.isDeleted !== true,
  IsDeleted: isDeleted ? true : x?.isDeleted === true,
});

export function toSavePayloads(map, removedItems = []) {
  const parentFieldMapsByEntityFieldId = map?.parentFieldMapsByEntityFieldId || {};
  const grouped = new Map();

  const addGroupedRow = (item, isDeleted = false) => {
    const entityFieldId = item?.entityFieldId || item?.raw?.EntityObjectFieldId || item?.raw?.EntityFieldId;
    if (!entityFieldId) return;
    const entry = grouped.get(entityFieldId) || [];
    entry.push(toSourceFieldMapRow(item, isDeleted, map?.sourceObjectId || null));
    grouped.set(entityFieldId, entry);
  };

  asArray(map?.items).forEach((item) => addGroupedRow(item, false));
  asArray(removedItems).forEach((item) => addGroupedRow(item, true));

  return Array.from(grouped.entries()).map(([entityFieldId, fieldMapRows]) => {
    const parentRaw = parentFieldMapsByEntityFieldId?.[entityFieldId] || {};
    const parentId = parentRaw?._id || parentRaw?.UniqueId || parentRaw?.Id || parentRaw?.id || null;
    const fallbackImportance = normalizeImportance(parentRaw?.Importance, 255);
    const sourceObjectIds = Array.from(
      new Set(
        fieldMapRows
          .map((row) => row?.SourceObjectId)
          .filter((id) => Boolean(id) && id !== EMPTY_GUID)
      )
    );
    const parentSourceObjectId =
      sourceObjectIds[0] ||
      map?.sourceObjectId ||
      parentRaw?.SourceObjectId ||
      undefined;

    return {
      ...parentRaw,
      _id: parentId || undefined,
      EntityObjectId: map?.entityObjectId || parentRaw?.EntityObjectId,
      EntityObjectFieldId: entityFieldId,
      SourceObjectId: parentSourceObjectId,
      SourceObjectIds: sourceObjectIds,
      UseForMapping: parentRaw?.UseForMapping === true,
      Importance: fallbackImportance,
      FieldMap: fieldMapRows,
      Items: fieldMapRows,
      IsActive: parentRaw?.IsActive !== false,
      IsDeleted: parentRaw?.IsDeleted === true,
    };
  });
}

export function aggregateFieldMapsForSource(fieldMaps, entityObjectId, sourceObjectId, parentFieldMapsByEntityFieldId = {}) {
  const nextMap = buildEmptyFieldMap(entityObjectId, sourceObjectId);
  nextMap.parentFieldMapsByEntityFieldId = { ...parentFieldMapsByEntityFieldId };

  asArray(fieldMaps).forEach((fieldMap) => {
    if (fieldMap?.IsDeleted === true || fieldMap?.isDeleted === true || fieldMap?.IsActive === false || fieldMap?.isActive === false) {
      return;
    }
    const entityFieldId = fieldMap?.EntityObjectFieldId || fieldMap?.entityFieldId;
    if (entityFieldId) {
      nextMap.parentFieldMapsByEntityFieldId[entityFieldId] = fieldMap;
    }

    asArray(fieldMap?.FieldMap).forEach((sourceMap) => {
      const sourceObjectIdForRow = sourceMap?.SourceObjectId || sourceMap?.sourceObjectId;
      if (sourceObjectIdForRow !== sourceObjectId) return;
      const normalizedItem = normalizeMapItem(sourceMap, entityFieldId);
      if (
        normalizedItem?.isDeleted === true ||
        !normalizedItem?.entityFieldId ||
        (!normalizedItem?.sourceFieldId && normalizedItem?.isConstant !== true)
      ) {
        return;
      }

      nextMap.items.push({
        ...normalizedItem,
        sourceObjectId: sourceObjectIdForRow,
        raw: sourceMap,
      });
    });
  });

  nextMap.items = ensureUniqueMapItemIds(nextMap.items);

  return nextMap;
}

export function toSavePayload(map, removedItems = []) {
  const base = map?.raw ? { ...map.raw } : {};
  const id = map?.id || firstId(base);
  const items = asArray(map?.items).map((x) => toSourceFieldMapRow(x, false));
  const deleted = asArray(removedItems).map((x) => toSourceFieldMapRow(x, true));

  return {
    ...base,
    _id: id || base?._id,
    EntityObjectId: map.entityObjectId,
    SourceObjectId: map.sourceObjectId,
    Items: [...items, ...deleted],
    FieldMap: [...items, ...deleted],
  };
}

export function mapDiff(currentItems, suggestedItems) {
  const current = asArray(currentItems);
  const next = asArray(suggestedItems);
  const bySourceCurrent = new Map(current.map((x) => [x.sourceFieldId, x]));
  const bySourceNext = new Map(next.map((x) => [x.sourceFieldId, x]));

  let added = 0;
  let removed = 0;
  let replaced = 0;

  bySourceNext.forEach((nextItem, sourceId) => {
    const cur = bySourceCurrent.get(sourceId);
    if (!cur) {
      added += 1;
      return;
    }
    if (cur.entityFieldId !== nextItem.entityFieldId) replaced += 1;
  });
  bySourceCurrent.forEach((_cur, sourceId) => {
    if (!bySourceNext.has(sourceId)) removed += 1;
  });

  return { added, removed, replaced };
}
